import React from "react";
import { Trophy, Calendar, Edit, BarChart3, MessageSquare, Settings, CheckCircle, Plus, Minus, Users, LogOut, Heart, Download } from "lucide-react";
import html2pdf from 'html2pdf.js';

export default function PlayerGuide() {
  const handleDownloadPDF = () => {
    // Hide the button before generating PDF
    const button = document.querySelector('.download-button-container');
    if (button) button.style.display = 'none';
    
    const element = document.getElementById('guide-content');
    const opt = {
      margin: 0.5,
      filename: 'Golf-Eclectic-Player-Guide.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    
    html2pdf().set(opt).from(element).save().then(() => {
      // Show button again after PDF is generated
      if (button) button.style.display = 'block';
    });
  };

  return (
    <div id="guide-content" className="max-w-4xl mx-auto space-y-8">
      {/* Welcome Section with Download Button */}
      <div className="text-center space-y-2">
        {/* Golf Eclectic Logo - Shows everywhere */}
        <img 
          src="/logo.png" 
          alt="Golf Eclectic Logo" 
          className="w-24 h-24 mx-auto rounded-full object-cover"
        />
        <h1 className="text-xl font-semibold text-ink tracking-tight">Player's Guide to Golf Eclectic</h1>
        <p className="text-lg text-ink-muted">Everything you need to know to track your best scores</p>
        
        {/* Download Button - Hidden in PDF */}
        <div className="download-button-container">
          <button
            onClick={handleDownloadPDF}
            className="mt-4 inline-flex items-center px-6 py-3 bg-ink hover:bg-ink-hover text-white font-semibold rounded-lg shadow-md transition-colors"
          >
            <Download className="w-5 h-5 mr-2" />
            Download as PDF
          </button>
        </div>
      </div>

      {/* UI Overview */}
      <section className="bg-accent-tint-2 border border-line rounded-lg p-6">
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4">🎨 Modern Interface</h2>
        <p className="text-ink-muted mb-3">
          Golf Eclectic features a clean, modern interface with intuitive +/- buttons for easy score entry.
        </p>
        <ul className="space-y-2 text-ink-muted">
          <li>• <span className="font-semibold">Responsive Design:</span> Works seamlessly on desktop, tablet, and mobile</li>
          <li>• <span className="font-semibold">+/- Buttons:</span> Large +/- buttons for easy score adjustment</li>
          <li>• <span className="font-semibold">Visual Feedback:</span> Green borders for improvements, red for worse scores</li>
          <li>• <span className="font-semibold">Achievement Highlights:</span> Yellow for hole-in-one 🏆, green for excellent 2 💎</li>
          <li>• <span className="font-semibold">Best Score Display:</span> Always shows your current best score under each hole</li>
          <li>• <span className="font-semibold">Smart Pre-fill:</span> Round 2+ automatically starts with your current best scores</li>
          <li>• <span className="font-semibold">Achievement Badges:</span> Competition cards show your trophies and diamonds earned</li>
        </ul>
      </section>

      {/* What is Eclectic */}
      <section className="bg-app border border-line rounded-lg p-6">
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <Trophy className="w-6 h-6 text-accent-deep" />
          What is Eclectic Golf?
        </h2>
        <p className="text-ink-muted mb-3">
          Eclectic golf is a scoring format where you build your best possible score by keeping the lowest score 
          you've ever achieved on each hole across multiple rounds.
        </p>
        <div className="bg-white rounded p-4 mt-3">
          <p className="font-semibold text-ink mb-2">Example:</p>
          <ul className="space-y-1 text-ink-muted">
            <li>• Round 1, Hole 1: Score 5</li>
            <li>• Round 2, Hole 1: Score 4 → <span className="font-bold text-accent-deep">✓ Improved! Your best is now 4</span></li>
            <li>• Round 3, Hole 1: Score 6 → <span className="text-ink-muted">Best stays at 4 (you'll see "⚠️ Worse than best")</span></li>
          </ul>
        </div>
      </section>

      {/* Getting Started */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4">🚀 Getting Started</h2>
        <div className="space-y-4">
          <div className="bg-white border-l-4 border-accent p-4 rounded">
            <h3 className="font-bold text-lg text-ink mb-2">1. Browse Competitions</h3>
            <p className="text-ink-muted">View available competitions on your dashboard. Each card shows:</p>
            <ul className="list-disc list-inside text-ink-muted ml-4 mt-2">
              <li>Competition dates and round limits</li>
              <li>Number of players registered</li>
              <li>Your progress (if joined)</li>
            </ul>
          </div>

          <div className="bg-white border-l-4 border-accent p-4 rounded">
            <h3 className="font-bold text-lg text-ink mb-2">2. Join a Competition</h3>
            <p className="text-ink-muted">Click "Join" on any competition card. A confirmation dialog will appear explaining:</p>
            <ul className="list-disc list-inside text-ink-muted ml-4 mt-2">
              <li>You can withdraw later if needed</li>
              <li>Withdrawing will permanently delete all your data from that competition</li>
            </ul>
          </div>

          <div className="bg-white border-l-4 border-accent p-4 rounded">
            <h3 className="font-bold text-lg text-ink mb-2">3. Enter Your First Round</h3>
            <p className="text-ink-muted">Click "Enter Score" and fill in:</p>
            <ul className="list-disc list-inside text-ink-muted ml-4 mt-2">
              <li><strong>Date:</strong> When you played (must be within competition dates)</li>
              <li><strong>Course Handicap:</strong> Your handicap for that round</li>
              <li><strong>Scores:</strong> Use +/- buttons for each of the 18 holes</li>
            </ul>
            <p className="text-ink-muted mt-2"><strong>Important:</strong> All holes must have scores (no zeros allowed)</p>
          </div>
        </div>
      </section>

      {/* Score Entry Features */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <Edit className="w-6 h-6 text-accent" />
          Score Entry Features
        </h2>
        <div className="space-y-3">
          <div className="bg-white border border-line rounded p-4">
            <h3 className="font-semibold text-ink mb-2">🎯 Visual Score Indicators</h3>
            <ul className="space-y-2 text-ink-muted text-sm">
              <li>• <span className="text-accent-deep font-bold">Green Border:</span> You've improved your best score on this hole</li>
              <li>• <span className="text-red-600 font-bold">Red Border:</span> Score is worse than your current best</li>
              <li>• <span className="text-warm font-bold">Yellow Highlight:</span> Confirmed hole-in-one 🏆</li>
              <li>• <span className="text-accent-deep font-bold">Green Highlight:</span> Excellent score of 2 💎</li>
              <li>• <span className="text-ink-muted font-bold">Gray:</span> First round (no previous best to compare)</li>
            </ul>
          </div>

          <div className="bg-white border border-line rounded p-4">
            <h3 className="font-semibold text-ink mb-2">🏆 Hole-in-One Detection</h3>
            <p className="text-ink-muted text-sm mb-2">
              When you enter a score of 1 on any hole:
            </p>
            <ul className="space-y-1 text-ink-muted text-sm">
              <li>• A confirmation dialog will appear when you submit</li>
              <li>• If confirmed, a celebration message is posted to the competition</li>
              <li>• The hole-in-one is highlighted in yellow on your scorecard</li>
              <li>• Achievement displays on your Best Scores page with 🏆 icon</li>
              <li>• Trophy count appears on competition card badge</li>
              <li>• <strong>Visible in leaderboard:</strong> When viewing other players' best scores, you'll see their 🏆 achievements too!</li>
            </ul>
          </div>

          <div className="bg-white border border-accent/30 rounded p-4">
            <h3 className="font-semibold text-ink mb-2">💎 Excellent 2 Recognition</h3>
            <p className="text-ink-muted text-sm mb-2">
              When the admin enables "Recognize Score of 2" for a competition:
            </p>
            <ul className="space-y-1 text-ink-muted text-sm">
              <li>• Admin enables "Recognize Score of 2" when creating or editing a competition</li>
              <li>• When you score 2, you get a confirmation dialog</li>
              <li>• If confirmed, an "Excellent 2!" celebration message is posted</li>
              <li>• Score of 2 is highlighted in green on your scorecard</li>
              <li>• Achievement displays with 💎 diamond icon</li>
              <li>• Diamond count appears on competition card badge</li>
              <li>• <strong>Visible in leaderboard:</strong> When viewing other players' best scores, you'll see their 💎 achievements too!</li>
              <li>• Historical achievements: Both trophies and diamonds are counted across all rounds</li>
            </ul>
          </div>

          <div className="bg-white border border-line rounded p-4">
            <h3 className="font-semibold text-ink mb-2">📊 Smart Pre-fill (Round 2+)</h3>
            <p className="text-ink-muted text-sm">
              After your first round, subsequent rounds automatically start with your current best scores, 
              making it easy to see which holes you need to improve.
            </p>
          </div>

          <div className="bg-white border border-red-200 rounded p-4">
            <h3 className="font-semibold text-ink mb-2">⚠️ Input Validation & Error Messages</h3>
            <p className="text-ink-muted text-sm mb-2">
              Clear, prominent error dialogs help you fix issues before submitting:
            </p>
            <ul className="space-y-1 text-ink-muted text-sm">
              <li>• <strong>Missing Date:</strong> Error dialog appears if you forget to select a round date</li>
              <li>• <strong>Invalid Date Range:</strong> Date must be within competition start and end dates</li>
              <li>• <strong>Future Dates:</strong> Cannot enter rounds for future dates</li>
              <li>• <strong>Invalid Handicap:</strong> Course handicap must be between 0 and 54</li>
              <li>• <strong>Incomplete Scores:</strong> All holes must have scores (no zeros)</li>
              <li>• <strong>Invalid Score Range:</strong> Each hole score must be between 1 and 10</li>
              <li>• <strong>Large, impossible-to-miss dialogs</strong> with red warning icon</li>
              <li>• <strong>Clear explanations</strong> of what needs to be fixed</li>
              <li>• <strong>Auto-scroll to top</strong> to ensure dialog visibility on mobile</li>
              <li>• <strong>Requires acknowledgment</strong> - click "OK, I'll Fix That" to dismiss</li>
            </ul>
            <p className="text-ink-muted text-xs mt-2 italic">
              Mobile-friendly design ensures you never miss important validation messages.
            </p>
          </div>
        </div>
      </section>

      {/* Competition Social Features */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <Users className="w-6 h-6 text-accent" />
          Competition Social Feed (NEW!)
        </h2>
        <p className="text-ink-muted mb-4">
          Connect with other players in your competition through the Social tab.
        </p>
        <div className="space-y-3">
          <div className="bg-white border border-line rounded p-4">
            <h3 className="font-semibold text-ink mb-2">💬 Post & Reply</h3>
            <ul className="space-y-1 text-ink-muted text-sm">
              <li>• Share updates, tips, and encouragement (max 500 characters)</li>
              <li>• Reply to other players' posts for threaded conversations</li>
              <li>• Delete your own posts and replies anytime</li>
            </ul>
          </div>

          <div className="bg-white border border-line rounded p-4">
            <h3 className="font-semibold text-ink mb-2">❤️ Reactions</h3>
            <p className="text-ink-muted text-sm mb-2">React to posts with four options:</p>
            <ul className="space-y-1 text-ink-muted text-sm">
              <li>• 👍 <strong>Like</strong> - Show approval</li>
              <li>• ❤️ <strong>Love</strong> - Strong appreciation</li>
              <li>• 🎉 <strong>Celebrate</strong> - Perfect for hole-in-ones!</li>
              <li>• 👏 <strong>Clap</strong> - Applause for great shots</li>
            </ul>
            <p className="text-ink-muted text-sm mt-2">
              Click a reaction to add it, click again to remove, or click a different one to switch.
            </p>
          </div>

          <div className="bg-white border border-line rounded p-4">
            <h3 className="font-semibold text-ink mb-2">⚙️ Privacy Control</h3>
            <p className="text-ink-muted text-sm">
              Go to Dashboard → Settings to enable/disable social feed per competition. You control which competitions you engage with socially.
            </p>
          </div>
        </div>
      </section>

      {/* Viewing Your Progress */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-accent" />
          Viewing Your Progress
        </h2>
        <div className="space-y-3">
          <div className="bg-white border border-line rounded p-4">
            <h3 className="font-semibold text-ink mb-2">Eclectic Score (Best Scores)</h3>
            <p className="text-ink-muted text-sm">
              View your best score for each of the 18 holes, when you achieved it, and your total eclectic score. 
              Hole-in-ones are highlighted with a yellow background and 🏆 icon.
            </p>
          </div>

          <div className="bg-white border border-line rounded p-4">
            <h3 className="font-semibold text-ink mb-2">All Rounds</h3>
            <p className="text-ink-muted text-sm">
              Review all rounds you've played with options to view details, edit, or delete rounds.
            </p>
          </div>

          <div className="bg-white border border-line rounded p-4">
            <h3 className="font-semibold text-ink mb-2">Leaderboard</h3>
            <p className="text-ink-muted text-sm">
              See where you rank against other players based on net scores (gross score minus handicap ÷ 2).
            </p>
          </div>
        </div>
      </section>

      {/* Leaving a Competition */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <LogOut className="w-6 h-6 text-red-600" />
          Leaving a Competition
        </h2>
        <div className="bg-red-50 border border-red-200 rounded p-4">
          <p className="text-ink-muted mb-3">
            You can leave any competition from either the competition list or within the competition view using the "Leave Competition" button.
          </p>
          <p className="text-red-700 font-semibold mb-2">⚠️ Important:</p>
          <ul className="space-y-1 text-ink-muted text-sm">
            <li>• All your rounds and scores will be permanently deleted</li>
            <li>• You'll be removed from the leaderboard</li>
            <li>• This action cannot be undone</li>
            <li>• You can rejoin later, but will start fresh</li>
          </ul>
        </div>
      </section>

      {/* Settings & Preferences */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <Settings className="w-6 h-6 text-accent" />
          Settings & Preferences
        </h2>
        <div className="space-y-3">
          <div className="bg-white border border-line rounded p-4">
            <h3 className="font-semibold text-ink mb-2">Admin Messages</h3>
            <p className="text-ink-muted text-sm">
              Toggle on/off to control receiving messages from admins for each competition.
            </p>
          </div>

          <div className="bg-white border border-line rounded p-4">
            <h3 className="font-semibold text-ink mb-2">Social Feed</h3>
            <p className="text-ink-muted text-sm">
              Enable or disable social feed participation for each of your active competitions independently.
            </p>
          </div>
        </div>
      </section>

      {/* Tips for Success */}
      <section className="bg-app border border-line rounded-lg p-6">
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4">💡 Tips for Success</h2>
        <ul className="space-y-2 text-ink-muted">
          <li>• <strong>Play Regularly:</strong> More rounds = more chances to improve your eclectic score</li>
          <li>• <strong>Focus on Weak Holes:</strong> Target holes where your best score is still high</li>
          <li>• <strong>Track Progress:</strong> Use the "All Rounds" tab to see your improvement over time</li>
          <li>• <strong>Stay Connected:</strong> Use the social feed to share tips and celebrate achievements</li>
          <li>• <strong>Check Leaderboard:</strong> See how you stack up and stay motivated</li>
          <li>• <strong>Handicap Matters:</strong> Your net score (gross - handicap ÷ 2) determines leaderboard position</li>
        </ul>
      </section>

      {/* Support */}
      <section className="text-center bg-app rounded-lg p-6">
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-3">Need Help?</h2>
        <p className="text-ink-muted">
          Contact your competition administrator for assistance with scores, registration, or any technical issues.
        </p>
      </section>
    </div>
  );
}
